<template>
  <div class="main-content">
    <!-- Title -->
    <div>
      <b-row class="mb-16">
        <b-col md="8">
          <h2 class="page-title">
            Client Summary
          </h2>
        </b-col>
      </b-row>
    </div>
    <!-- Filters -->
    <b-row class="filters">
      <b-col md="6">
        <b-form-group label="Date">
          <v-date-picker
            v-model="dateRange"
            :first-day-of-week="1"
            mode="range"
            :input-props="{
              placeholder: 'Please select date range'
            }"
            :masks="{ input: 'YYYY/MM/DD' }"
          ></v-date-picker>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Client">
          <b-form-select v-model="client" :options="clientOptions">
            <template v-slot:first>
              <option :value="null">-- Please select a Client --</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-row>
        <b-col md="10">
          <b-button
            pill
            variant="outline-primary ripple"
            class="clear-btn"
            size="sm"
            @click="clear"
            >Clear</b-button
          >
        </b-col>
      </b-row>
    </b-row>

    <!-- Summary Table -->
    <b-row class="table-row">
      <overflowMessage ref="overflowMessage" ref-name="userTimeLogs" />

      <b-table
        ref="userTimeLogs"
        :busy="summaryTableLoading"
        show-empty
        empty-text="No timeLogs found"
        hover
        bordered
        striped
        thead-class="cursor-pointer"
        sticky-header
        responsive
        :fields="fields"
        :items="items"
        :current-page="currentPage"
        :per-page="perPage"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            variant="success"
            @click="showTimeLogModal(row.item)"
          >
            Edit
          </b-button>
        </template>
        <template v-slot:table-busy>
          <TableLoadingSkeleton />
        </template>
      </b-table>
      <Pagination
        :total-rows="totalRows"
        :page-options="pageOptions"
        :per-page="perPage"
        :current-page="currentPage"
      />
    </b-row>
  </div>
</template>

<script>
import momentBusiness from "moment-business-days"
import overflowMessage from "@/components/misc/overflowMessage"
import TableLoadingSkeleton from "@/components/misc/tableLoadingSkeleton"
import gql from "graphql-tag"
import Pagination from "@/components/misc/customPagination"

export default {
  name: "ClientSummary",
  components: {
    overflowMessage,
    TableLoadingSkeleton,
    Pagination
  },
  apollo: {
    clients: {
      query: gql`
        query clients($start: Date, $end: Date) {
          clients {
            id
            name
            supportTime
            timeLogs(start: $start, end: $end) {
              time
              date
              type
              support
            }
          }
        }
      `,
      variables() {
        //This is put here because this is hit right when the query is called
        this.summaryTableLoading = true
        return {
          start: this.dateRange?.start.asGMT(),
          end: this.dateRange?.end.asGMT()
        }
      },
      update(data) {
        //This is put here because this is hit right when the query result is returned
        this.summaryTableLoading = false

        return data?.clients.sortBy(x => x.name)
      },
      skip() {
        return !this.dateRange
      }
    }
  },
  data() {
    return {
      dateRange: null,
      summaryTableLoading: true,
      fields: null,
      client: null,
      currentPage: 1,
      perPage: 15,
      pageOptions: [5, 10, 15]
    }
  },
  computed: {
    clientOptions() {
      return this.clients?.map(client => {
        return { value: client, text: client.name }
      })
    },
    items() {
      return this.clients
        ?.filter(x => !this.client || x.id == this.client.id)
        .map(client => {
          return {
            Client: client.name,
            Allocated: client.supportTime,
            Remaining: this.getRemainingHours(client),
            Other: this.getRemainingHours(client),
            Billable: this.getBillableHours(client),
            NonBillable: this.getNonBillableHours(client)
          }
        })
    },
    totalRows() {
      return this.items?.length ?? 0
    }
  },
  created() {
    this.setDateRange()
    this.fields = [
      { key: "Client", sortable: true },
      { key: "Allocated", sortable: true },
      { key: "Remaining", sortable: true },
      { key: "Billable", sortable: true },
      { key: "NonBillable", sortable: true },
      { key: "Other", sortable: true }
    ]
  },
  methods: {
    /**Clears filters */
    clear() {
      this.setDateRange()
      this.client = null
    },
    /**
     * Gets the remaining Billable hours for client
     * @param client
     * @returns {number}
     */
    getRemainingHours(client) {
      let total = 0
      if (!client.timeLogs?.some(x => x)) total = client.supportTime
      else
        client.timeLogs.forEach(c => {
          total +=
            (c.type === "Billable" || c.type === "Unsure") && c.support
              ? c.time
              : 0 || 0
        })
      total = client.supportTime - total
      total = parseFloat(total.toFixed(2))
      client.remaining = total
      return total
    },
    /**
     * Gets Non-Billable/Non-Support hours logged for client
     * @param client
     * @returns {number}
     */
    getOtherHours(client) {
      let total = 0
      if (!client.timeLogs?.some(x => x)) return total
      else
        client.timeLogs.forEach(c => {
          total += (!c.support ? c.time : 0) || 0
        })

      return parseFloat(total.toFixed(2))
    },
    /**
     * Gets the total Billable hours for client
     * @param client
     * @returns {number}
     */
    getBillableHours(client) {
      let total = 0
      if (!client.timeLogs?.some(x => x)) return total
      else
        client.timeLogs.forEach(c => {
          total += (c.type === "Billable" ? c.time : 0) || 0
        })

      return parseFloat(total.toFixed(2))
    },
    /**
     * Gets the total NonBillable hours for client
     * @param client
     * @returns {number}
     */
    getNonBillableHours(client) {
      let total = 0
      if (!client.timeLogs?.some(x => x)) return total
      else
        client.timeLogs.forEach(c => {
          total += (c.type === "Non-Billable" ? c.time : 0) || 0
        })

      return parseFloat(total.toFixed(2))
    },
    /**
     * Set default date range to current month
     */
    setDateRange() {
      this.dateRange = {
        start: this.$moment(new Date())
          .subtract(1, "months")
          .endOf("month")
          .toDate(),
        end: momentBusiness(new Date(), "YYYY-DD-MM")
          .prevBusinessDay()
          .toDate()
      }
    }
  }
}
</script>

<style scoped>
.filters {
  margin-bottom: 2rem;
}
.clear-btn {
  margin: 1rem 0 0 1rem;
}
</style>
